import React, { useContext, useState } from 'react'
import { Button } from '@/components/ui/button'
import { GridColumn as Column, Grid } from '@progress/kendo-react-grid'
import { Upload } from 'antd'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import {
  LeverageInstrumentDto,
  UploadLeverageInstrumentDto,
} from 'api/Trading-Accounts/Types/InstrumentDto'
import axios from 'axios'
import { FlexBox } from 'components/Box'
import { Modal } from 'components/CustomModal'
import { errorToast, infoToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { hasPermission } from 'utils/permissionsValidator'

interface Props {
  onClose(): void
  leverageTypes: LeverageInstrumentDto[]
  permissions: PermissionDto[]
  platformId?: number
  serverId?: string
}

const FileUploadActionsStyle = { marginLeft: 4, marginBottom: 24, gap: 8 }
const SubmitButtonStyle = { marginTop: 48, marginBottom: 48 }

export const UploadLeverageModal: React.FC<Props> = (props: Props) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [leverageFilePreview, setLeverageFilePreview] = useState<UploadLeverageInstrumentDto[]>()
  const [fileInfo, setFileInfo] = useState<File>()
  const setToast = useContext(ToastContext)
  const isFileSelected = !!fileInfo

  const handleUploadLeverageFile = async () => {
    try {
      if (fileInfo) {
        const formData = new FormData()
        formData.append('file', fileInfo)
        const response = await tradingAccountsApiClient.uploadLeveragePreview(formData)
        setLeverageFilePreview(response)
      }
    } catch (e: unknown) {
      console.error(e)
      if (axios.isAxiosError(e)) {
        const err = e.response?.data?.message
        if (err) {
          setToast(errorToast(`Error: ${err}`))
        } else {
          setToast(errorToast('Error uploading leverage file.'))
        }
      } else {
        setToast(errorToast('An unexpected error occurred.'))
      }
    }
  }

  const handleSelectFile = (file: File) => {
    if (file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel') {
      setToast(infoToast('Supported format is only csv.'))
      return false
    }
    setFileInfo(file)
    return false
  }

  const handleConfirmationSubmit = async () => {
    if (!hasPermission(props.permissions, PermissionsEnum.CanEditInstrument))
      return setToast(errorToast('Permission denied'))

    if (fileInfo) {
      try {
        setToast(infoToast('Uploading leverage file ...'))
        const formData = new FormData()
        formData.append('file', fileInfo)

        if (props?.platformId && props?.serverId) {
          await tradingAccountsApiClient.uploadLeverageFile(
            props?.platformId,
            props?.serverId,
            formData
          )
          setToast(successToast('Leverage has been uploaded.'))
          props.onClose()
        } else {
          setToast(errorToast('Error select a source server'))
        }
      } catch (error: unknown) {
        console.error(error)

        if (axios.isAxiosError(error)) {
          const err = error.response?.data?.message
          if (err) {
            setToast(errorToast(`Error: ${err}`))
          } else {
            setToast(errorToast('Error uploading leverage file.'))
          }
        } else {
          setToast(errorToast('An unexpected error occurred.'))
        }
      }
    }
  }

  return (
    <Modal width={1200} visible={true} onClose={props.onClose}>
      <FlexBox style={FileUploadActionsStyle}>
        <Upload beforeUpload={handleSelectFile} showUploadList={false}>
          <Button type='button'>{fileInfo?.name ? fileInfo?.name : <>Select File ...</>}</Button>
        </Upload>
        <Button type='button' onClick={handleUploadLeverageFile} disabled={!isFileSelected}>
          Upload
        </Button>
      </FlexBox>

      <Grid data={leverageFilePreview} editField='inEdit' resizable>
        <Column field='symbol' title='Symbol' filter='text' minResizableWidth={200} width={200} />
        <Column
          field='leverageTypeId'
          title='Leverage'
          minResizableWidth={200}
          width={200}
          cell={(gridCellProps) => {
            const { leverageTypeId } = gridCellProps.dataItem

            const foundLeverage = props.leverageTypes?.find((type) => {
              return type.id === leverageTypeId
            })

            return <td>{foundLeverage?.name}</td>
          }}
        />
      </Grid>
      <FlexBox style={SubmitButtonStyle} alignItems='left' justifyContent='left'>
        <Button
          disabled={!leverageFilePreview}
          type='button'
          onClick={async () => await handleConfirmationSubmit()}
        >
          Submit
        </Button>
      </FlexBox>
    </Modal>
  )
}
